import { inject, InjectionToken } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { AccountGroupService, ProjectionFilter, ReadFilter } from '@galaxy/account-group';
import { FeatureFlagService } from '@galaxy/partner';
import { PackageService } from '@vendasta/marketplace-packages';
import { CoreOrderConfig, OrderBusinessConfig, OrderPermissions, PageOrderConfig } from '@vendasta/orders';
import { User } from '@vendasta/store';
import { firstValueFrom, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { AppConfigService } from '../app-config.service';
import { StubAccessService } from '../core/access';
import { Views } from '../core/access/interface';
import { PlatformPermissionsService } from '../core/access/platform-permissions.service';
import { AccountService } from '../core/account.service';
import { FeatureFlags } from '../core/features';

export const USER_ID_TOKEN = new InjectionToken<string>('User ID to retrieve salesperson role');

export function coreOrderConfigFactory(partnerId$: Observable<string>): CoreOrderConfig {
  const featureFlagsService = inject(FeatureFlagService);
  const packageService = inject(PackageService);
  const accountGroupService = inject(AccountGroupService);
  const accountsService = inject(AccountService);

  const automationsOrderWorkflowEnabled$ = partnerId$.pipe(
    switchMap((partnerId) =>
      featureFlagsService.batchGetStatus(partnerId, '', [FeatureFlags.AUTOMATIONS_ORDER_WORKFLOW]),
    ),
    map((featureFlags) => featureFlags && featureFlags[FeatureFlags.AUTOMATIONS_ORDER_WORKFLOW]),
    take(1),
    shareReplay(1),
  );
  const getMultiPackages = (packageIds: string[]) => packageService.getMulti(packageIds).pipe(shareReplay(1));
  const listenToBusiness = (businessId: string, mask: ProjectionFilter, readFilter?: ReadFilter) =>
    accountGroupService.getMulti([businessId], mask, readFilter).pipe(
      map((results) => results?.[0]),
      catchError(() => of(null)),
      take(1),
      shareReplay(1),
    );
  const listenToBusinessUsers = (partnerId: string, businessId: string) =>
    accountsService.loadUsers(businessId, partnerId, []).pipe(
      map((users) => users?.map((u) => <User>u) ?? []),
      catchError(() => of([])),
      take(1),
      shareReplay(1),
    );

  return {
    automationsOrderWorkflowEnabled$,
    getMultiPackages,
    listenToBusiness,
    listenToBusinessUsers,
  };
}

export function pageOrderConfigFactory(): PageOrderConfig {
  const activatedRoute = inject(ActivatedRoute);
  const viewAccessService = inject(StubAccessService);
  const appConfigService = inject(AppConfigService);

  const businessId$ = activatedRoute.paramMap.pipe(
    map((params) => params?.get('businessId') ?? ''),
    takeUntilDestroyed(),
    distinctUntilChanged(),
  );
  const orderId$ = activatedRoute.paramMap.pipe(
    map((params) => params?.get('orderId') ?? ''),
    takeUntilDestroyed(),
    distinctUntilChanged(),
  );
  const orderPermissions$: Observable<OrderPermissions> = viewAccessService
    .hasAccessToViews([Views.viewManageSalesOrders, Views.marketplace])
    .pipe(
      map((resp) => {
        return {
          accessMarketplace: resp.get(Views.marketplace),
          accessManageOrders: resp.get(Views.viewManageSalesOrders),
        };
      }),
      take(1),
      shareReplay(1),
    );
  const userCanAccessRetailBilling$ = appConfigService.config$.pipe(map((config) => config.userCanAccessRetailBilling));
  const userCanAccessWholeSaleBilling$ = appConfigService.config$.pipe(map((config) => config.userCanAccessBilling));
  return {
    businessId$,
    orderId$,
    previousPageUrl$: of('/order-management'),
    orderPermissions$,
    userCanAccessRetailBilling$,
    userCanAccessWholeSaleBilling$,
    invoicesPageUrl$: of('/invoices'),
    getOrderUrl: (orderId: string, businessId: string): string => {
      return `/order-management/${businessId}/view/${orderId}`;
    },
  };
}

export function orderBusinessConfigFactory(): OrderBusinessConfig {
  const appConfigService = inject(AppConfigService);
  const platformPermissionsService = inject(PlatformPermissionsService);

  return {
    getBusinessUrl: async (businessId: string): Promise<string> => {
      const config = await firstValueFrom(appConfigService.config$);
      const permissions = await platformPermissionsService.getPermissions(config.partnerId, config.unifiedUserId);
      if (permissions.crmCompanies || config.isSuperAdmin) {
        return `/crm/company/redirect?accountGroupId=${businessId}`;
      } else if (config.userCanAccessAccounts) {
        return `/businesses/accounts/${businessId}/details`;
      }
      return '';
    },
  };
}
